/**
 * BroadcastTab — Admin manual notification sender
 * Send announcements / batch news / job alerts to targeted audiences.
 * Also shows: recent broadcast history, recent auto-detected lab changes.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { Send, Megaphone, GraduationCap, Briefcase, History, BookOpen, X, Plus, Pencil, Minus } from 'lucide-react';
import { API_BASE, getAdminToken } from './adminUtils';

const authFetch = (url, options = {}) => {
  const token = getAdminToken();
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    },
  });
};

const TYPE_OPTIONS = [
  { id: 'announcement', label: 'Announcement', icon: Megaphone, color: 'amber', desc: 'General news, offers, maintenance' },
  { id: 'batch_news',   label: 'Batch News',   icon: GraduationCap, color: 'emerald', desc: 'New batch starting, schedule changes' },
  { id: 'job_alert',    label: 'Job Alert',    icon: Briefcase,    color: 'purple',  desc: 'Placement / hiring opportunities' },
];

const SEVERITY_OPTIONS = [
  { id: 'info', label: 'Info', dot: 'bg-blue-500' },
  { id: 'warning', label: 'Warning', dot: 'bg-amber-500' },
  { id: 'critical', label: 'Critical', dot: 'bg-rose-500' },
];

const AUDIENCE_OPTIONS = [
  { id: 'all', label: 'All active users' },
  { id: 'course_enrolled', label: 'Users enrolled in specific courses' },
  { id: 'no_enrollment', label: 'Users with NO active enrollment (cold leads)' },
  { id: 'specific_users', label: 'Specific users (paste user IDs)' },
];

const COURSE_CODES = [
  'RH124', 'RH134', 'SVRADM', 'ANSIBLE', 'DOCKER', 'KUBERNETES', 'TERRAFORM', 'GIT',
  'PYTHON', 'C_LANG', 'CPP', 'JAVA', 'MYSQL', 'EXAM_RHCSA', 'EXAM_DO188', 'EXAM_EX294',
];

export const BroadcastTab = () => {
  // Form state
  const [type, setType] = useState('announcement');
  const [severity, setSeverity] = useState('info');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [audienceType, setAudienceType] = useState('all');
  const [targetCourses, setTargetCourses] = useState([]);
  const [targetUserIdsText, setTargetUserIdsText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // History state
  const [history, setHistory] = useState([]);
  const [labChanges, setLabChanges] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);
  // Drill-down modal for a selected lab change
  const [activeLabChange, setActiveLabChange] = useState(null);

  const loadHistory = async () => {
    setLoadingHist(true);
    try {
      const [hRes, lRes] = await Promise.all([
        authFetch(`${API_BASE}/api/admin/notifications/history?limit=20`),
        authFetch(`${API_BASE}/api/admin/notifications/lab-changes?limit=20`),
      ]);
      const h = await hRes.json().catch(() => ({}));
      const l = await lRes.json().catch(() => ({}));
      setHistory(h.broadcasts || []);
      setLabChanges(l.changes || []);
    } catch {
      setHistory([]); setLabChanges([]);
    } finally {
      setLoadingHist(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const toggleCourse = (c) => {
    setTargetCourses((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 3 || message.trim().length < 3) {
      toast.error('Title and message required (min 3 chars)');
      return;
    }
    if (audienceType === 'course_enrolled' && targetCourses.length === 0) {
      toast.error('Select at least one course for course-enrolled audience');
      return;
    }
    let userIds = [];
    if (audienceType === 'specific_users') {
      userIds = targetUserIdsText.split(/[\s,;\n]+/).map((s) => s.trim()).filter(Boolean);
      if (userIds.length === 0) {
        toast.error('Paste at least one email or user ID');
        return;
      }
    }

    setSubmitting(true);
    try {
      const body = {
        type, severity, title: title.trim(), message: message.trim(),
        link: link.trim() || null,
        audience_type: audienceType,
        target_courses: audienceType === 'course_enrolled' ? targetCourses : [],
        target_user_ids: audienceType === 'specific_users' ? userIds : [],
      };
      const res = await authFetch(`${API_BASE}/api/admin/notifications/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Broadcast failed');
        return;
      }
      toast.success(`Sent to ${data.delivered_to} users`);
      // reset form
      setTitle(''); setMessage(''); setLink('');
      setTargetCourses([]); setTargetUserIdsText('');
      loadHistory();
    } catch (err) {
      toast.error('Network error: ' + (err?.message || ''));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="broadcast-tab">
      {/* Composer */}
      <Card className="bg-gradient-to-br from-amber-500/5 to-purple-500/5 border-amber-500/20">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Send className="w-5 h-5 text-amber-300" />
            Compose Broadcast
          </CardTitle>
          <p className="text-slate-400 text-sm">Send a manual notification to your students. Rate-limited to 10 per hour.</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Type */}
            <div>
              <Label className="text-white text-sm">Notification Type</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                {TYPE_OPTIONS.map((t) => {
                  const Icon = t.icon;
                  const active = type === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setType(t.id)}
                      className={`flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                        active
                          ? `bg-${t.color}-500/20 border-${t.color}-400 text-white`
                          : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                      }`}
                      data-testid={`broadcast-type-${t.id}`}
                    >
                      <Icon className={`w-5 h-5 flex-shrink-0 ${active ? `text-${t.color}-300` : 'text-slate-400'}`} />
                      <div>
                        <div className="text-sm font-semibold">{t.label}</div>
                        <div className="text-xs opacity-80 mt-0.5">{t.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Severity */}
            <div>
              <Label className="text-white text-sm">Severity</Label>
              <div className="flex gap-2 mt-2">
                {SEVERITY_OPTIONS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSeverity(s.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border ${
                      severity === s.id
                        ? 'bg-white/15 border-white/30 text-white'
                        : 'bg-white/5 border-white/10 text-slate-400 hover:bg-white/10'
                    }`}
                    data-testid={`broadcast-severity-${s.id}`}
                  >
                    <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="bc-title" className="text-white text-sm">Title <span className="text-rose-400">*</span></Label>
              <Input
                id="bc-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Diwali Offer — 20% off All Access"
                maxLength={200}
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="broadcast-title"
              />
              <div className="text-xs text-slate-500 mt-0.5">{title.length} / 200</div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="bc-message" className="text-white text-sm">Message <span className="text-rose-400">*</span></Label>
              <Textarea
                id="bc-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Write the full message students will see in their inbox..."
                rows={4}
                maxLength={2000}
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="broadcast-message"
              />
              <div className="text-xs text-slate-500 mt-0.5">{message.length} / 2000</div>
            </div>

            {/* Link */}
            <div>
              <Label htmlFor="bc-link" className="text-white text-sm">Action Link (optional)</Label>
              <Input
                id="bc-link"
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/pricing or https://lab.grras.tech/..."
                className="mt-1 bg-white/5 border-white/10 text-white"
                data-testid="broadcast-link"
              />
              <div className="text-xs text-slate-500 mt-0.5">Internal path (/...) or HTTPS URL only</div>
            </div>

            {/* Audience */}
            <div>
              <Label className="text-white text-sm">Audience</Label>
              <div className="space-y-2 mt-2">
                {AUDIENCE_OPTIONS.map((a) => (
                  <label
                    key={a.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                      audienceType === a.id
                        ? 'bg-emerald-500/15 border-emerald-400 text-white'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                    data-testid={`broadcast-audience-${a.id}`}
                  >
                    <input
                      type="radio"
                      name="aud"
                      value={a.id}
                      checked={audienceType === a.id}
                      onChange={() => setAudienceType(a.id)}
                      className="accent-emerald-500"
                    />
                    <span className="text-sm">{a.label}</span>
                  </label>
                ))}
              </div>

              {audienceType === 'course_enrolled' && (
                <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/10">
                  <div className="text-xs text-slate-400 mb-2">Select target courses ({targetCourses.length} selected)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {COURSE_CODES.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => toggleCourse(c)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                          targetCourses.includes(c)
                            ? 'bg-emerald-500 border-emerald-400 text-white'
                            : 'bg-white/5 border-white/15 text-slate-300 hover:bg-white/10'
                        }`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {audienceType === 'specific_users' && (
                <>
                  <Textarea
                    value={targetUserIdsText}
                    onChange={(e) => setTargetUserIdsText(e.target.value)}
                    placeholder="Paste emails OR user IDs (one per line, or comma-separated)&#10;e.g. forti143@gmail.com&#10;e.g. 4694e290-807c-46fe-953c-3a680fdf0ddd"
                    rows={4}
                    className="mt-3 bg-white/5 border-white/10 text-white font-mono text-xs"
                    data-testid="broadcast-target-user-ids"
                  />
                  <div className="text-xs text-slate-400 mt-1">Tip: emails are easiest — system auto-resolves to user IDs.</div>
                </>
              )}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full px-4 py-3 rounded-lg bg-emerald-600 text-white font-bold hover:bg-emerald-700 transition disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="broadcast-submit"
            >
              <Send className="w-4 h-4" />
              {submitting ? 'Sending…' : 'Send Broadcast'}
            </button>
          </form>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <History className="w-5 h-5 text-slate-300" />
            Recent Broadcasts ({history.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingHist && <div className="text-sm text-slate-400">Loading…</div>}
          {!loadingHist && history.length === 0 && (
            <div className="text-sm text-slate-400 italic">No broadcasts yet.</div>
          )}
          {!loadingHist && history.length > 0 && (
            <div className="space-y-2">
              {history.map((b) => (
                <div key={b.id} className="p-3 rounded-lg border border-white/10 bg-black/20">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px]">{b.type}</Badge>
                        <Badge className="bg-slate-500/15 text-slate-300 border-slate-500/30 text-[10px]">{b.severity}</Badge>
                        <span className="text-[10px] text-slate-500">{b.audience_type}</span>
                      </div>
                      <div className="text-sm font-semibold text-white mt-1">{b.title}</div>
                    </div>
                    <div className="text-xs text-slate-400 text-right flex-shrink-0">
                      <div className="font-bold text-emerald-300">{b.delivered}</div>
                      <div>delivered</div>
                    </div>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{b.created_at ? new Date(b.created_at).toLocaleString() : ''}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lab Changes Auto-Detected */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-300" />
            Auto-Detected Lab Changes ({labChanges.length})
          </CardTitle>
          <p className="text-xs text-slate-400">When you edit YAML lab files, students get notified automatically and impacted scores are recalculated.</p>
        </CardHeader>
        <CardContent>
          {loadingHist && <div className="text-sm text-slate-400">Loading…</div>}
          {!loadingHist && labChanges.length === 0 && (
            <div className="text-sm text-slate-400 italic">No lab changes detected since system start.</div>
          )}
          {!loadingHist && labChanges.length > 0 && (
            <div className="space-y-2">
              {labChanges.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setActiveLabChange(c)}
                  data-testid={`lab-change-${c.id}`}
                  className="w-full text-left p-3 rounded-lg border border-white/10 bg-black/20 flex items-start justify-between gap-3 hover:bg-white/5 hover:border-blue-400/30 transition cursor-pointer"
                >
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-white">
                      {c.course_id} → {c.lab_id}
                      <Badge className="ml-2 bg-blue-500/15 text-blue-300 border-blue-500/30 text-[10px]">v{c.from_version} → v{c.to_version}</Badge>
                    </div>
                    <div className="text-xs text-slate-400 mt-1">
                      {c.added_count > 0 && <span className="text-emerald-300 mr-2">+{c.added_count} added</span>}
                      {c.modified_count > 0 && <span className="text-amber-300 mr-2">~{c.modified_count} modified</span>}
                      {c.removed_count > 0 && <span className="text-rose-300 mr-2">-{c.removed_count} removed</span>}
                      <span className="text-slate-500">{c.detected_at ? new Date(c.detected_at).toLocaleString() : ''}</span>
                    </div>
                    <div className="text-[11px] text-blue-300/70 mt-1">Click to view details &rarr;</div>
                  </div>
                  <div className="text-xs text-slate-400 text-right flex-shrink-0">
                    <div className="font-bold text-blue-300">{c.notified_users_count || 0}</div>
                    <div>notified</div>
                    {(c.scores_recalculated_for_users ?? 0) > 0 && (
                      <>
                        <div className="font-bold text-amber-300 mt-2">{c.scores_recalculated_for_users}</div>
                        <div>scores adj.</div>
                      </>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down Modal */}
      {activeLabChange && (
        <LabChangeDetailModal
          change={activeLabChange}
          onClose={() => setActiveLabChange(null)}
        />
      )}
    </div>
  );
};

export default BroadcastTab;

// =====================================================================
// Lab Change Detail Modal — drill-down for a single auto-detected change
// =====================================================================
const LabChangeDetailModal = ({ change, onClose }) => {
  const c = change || {};
  const added = c.added_tasks || [];
  const modified = c.modified_tasks || [];
  const removed = c.removed_tasks || [];

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="lab-change-modal"
    >
      <div
        className="bg-slate-900 border border-white/10 rounded-xl shadow-2xl w-full max-w-3xl my-8 max-h-[90vh] overflow-hidden flex flex-col"
        onClick={stop}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <BookOpen className="w-5 h-5 text-blue-300" />
              <h3 className="text-lg font-bold text-white">
                {c.course_id} → {c.lab_id}
              </h3>
              <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 text-[10px]">
                v{c.from_version} → v{c.to_version}
              </Badge>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              Detected {c.detected_at ? new Date(c.detected_at).toLocaleString() : ''} &middot;{' '}
              <span className="text-blue-300 font-semibold">{c.notified_users_count || 0}</span> notified
              {(c.scores_recalculated_for_users ?? 0) > 0 && (
                <>
                  {' · '}
                  <span className="text-amber-300 font-semibold">{c.scores_recalculated_for_users}</span> score{c.scores_recalculated_for_users !== 1 ? 's' : ''} recalculated
                </>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 w-8 h-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:bg-white/10 hover:text-white"
            aria-label="Close"
            data-testid="lab-change-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Summary chips */}
          <div className="flex items-center gap-2 flex-wrap">
            {added.length > 0 && (
              <span className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 inline-flex items-center gap-1">
                <Plus className="w-3 h-3" /> {added.length} added
              </span>
            )}
            {modified.length > 0 && (
              <span className="text-xs px-2 py-1 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 inline-flex items-center gap-1">
                <Pencil className="w-3 h-3" /> {modified.length} modified
              </span>
            )}
            {removed.length > 0 && (
              <span className="text-xs px-2 py-1 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30 inline-flex items-center gap-1">
                <Minus className="w-3 h-3" /> {removed.length} removed
              </span>
            )}
            {added.length === 0 && modified.length === 0 && removed.length === 0 && (
              <span className="text-xs text-slate-400 italic">No task-level details available for this change.</span>
            )}
          </div>

          {/* Added tasks */}
          {added.length > 0 && (
            <section>
              <h4 className="text-xs uppercase tracking-wider font-bold text-emerald-300 mb-2 inline-flex items-center gap-1.5">
                <Plus className="w-3.5 h-3.5" /> Added tasks
              </h4>
              <div className="space-y-2">
                {added.map((t) => (
                  <div key={t.id} className="p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                    <div className="text-xs text-emerald-300 font-mono mb-1">{t.id} &middot; {t.points} pts</div>
                    <div className="text-sm text-slate-200">{t.title}</div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Modified tasks */}
          {modified.length > 0 && (
            <section>
              <h4 className="text-xs uppercase tracking-wider font-bold text-amber-300 mb-2 inline-flex items-center gap-1.5">
                <Pencil className="w-3.5 h-3.5" /> Modified tasks
              </h4>
              <div className="space-y-2">
                {modified.map((m) => {
                  const titleChanged = m.old_title !== m.new_title;
                  const pointsChanged = m.old_points !== m.new_points;
                  return (
                    <div key={m.id} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="text-xs text-amber-300 font-mono">{m.id}</div>
                        {pointsChanged && (
                          <div className="text-[11px] text-amber-200">
                            <span className="line-through text-slate-500">{m.old_points} pts</span>
                            {' → '}
                            <span className="font-semibold">{m.new_points} pts</span>
                          </div>
                        )}
                      </div>
                      {titleChanged ? (
                        <>
                          <div className="text-xs text-slate-500 mb-0.5">Old title</div>
                          <div className="text-sm text-slate-400 line-through mb-2">{m.old_title || <em>(empty)</em>}</div>
                          <div className="text-xs text-slate-500 mb-0.5">New title</div>
                          <div className="text-sm text-slate-200">{m.new_title || <em>(empty)</em>}</div>
                        </>
                      ) : (
                        <div className="text-sm text-slate-200">{m.new_title || <em>(empty)</em>}</div>
                      )}
                      {!titleChanged && pointsChanged && (
                        <div className="text-[11px] text-slate-500 mt-1 italic">Points-only change — student progress preserved.</div>
                      )}
                      {titleChanged && (
                        <div className="text-[11px] text-amber-200/80 mt-2 italic">Title changed → completed students must re-attempt this task.</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Removed tasks */}
          {removed.length > 0 && (
            <section>
              <h4 className="text-xs uppercase tracking-wider font-bold text-rose-300 mb-2 inline-flex items-center gap-1.5">
                <Minus className="w-3.5 h-3.5" /> Removed tasks
              </h4>
              <div className="space-y-2">
                {removed.map((t) => (
                  <div key={t.id} className="p-3 rounded-lg bg-rose-500/5 border border-rose-500/20">
                    <div className="text-xs text-rose-300 font-mono mb-1">{t.id} &middot; {t.points} pts</div>
                    <div className="text-sm text-slate-300 line-through">{t.title}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 p-4 flex items-center justify-between gap-3 bg-black/20">
          <div className="text-[11px] text-slate-500">
            Change type: <span className="text-slate-300 font-mono">{c.change_type}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium"
            data-testid="lab-change-modal-close-footer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

